import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { api, type SourceLookupResult } from "@/lib/api";
import type { DMARCReport, DMARCRecord } from "@dmarc/shared";

type ReportWithRecords = DMARCReport & { domain: string; records: DMARCRecord[] };

export function ReportDetailPage() {
  const { reportId } = useParams({ strict: false });
  const [expandedIp, setExpandedIp] = useState<string | null>(null);

  const { data: report, isLoading, error } = useQuery<ReportWithRecords>({
    queryKey: ["report", reportId],
    queryFn: () => api.reports.get(reportId!),
    enabled: !!reportId,
  });

  // Get failed IPs for lookup
  const failedIps = report?.records?.filter(r => !r.dmarcPass).map(r => r.sourceIp) || [];

  // Lookup source information for failed IPs
  const { data: sourceLookups } = useQuery<Record<string, SourceLookupResult>>({
    queryKey: ["source-lookups", failedIps],
    queryFn: () => api.reports.lookupSources(failedIps),
    enabled: failedIps.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateRange = (begin: string, end: string) => {
    const beginDate = new Date(begin);
    const endDate = new Date(end);
    return `${beginDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading report details">
        <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-medium">Report not found</h2>
        <p className="mt-2 text-muted-foreground">This report may have been deleted or you don't have access.</p>
        <Link to="/reports" className="btn btn-primary mt-4">
          Back to Reports
        </Link>
      </div>
    );
  }

  const passRate = report.totalCount > 0
    ? Math.round((report.passCount / report.totalCount) * 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="page-header-icon">
          <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>
        <div>
          <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
            <Link to="/reports" className="hover:text-white">Reports</Link>
            <span>/</span>
            <span>{report.policyDomain}</span>
          </div>
          <h1 className="text-2xl font-bold">{report.orgName}</h1>
          <p className="text-white/80">{formatDateRange(report.dateRangeBegin, report.dateRangeEnd)}</p>
        </div>
        <div className="page-header-decor" />
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-primary">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Total Messages</div>
              <div className="stat-value">{report.totalCount.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-emerald">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Passed</div>
              <div className="stat-value text-success">{report.passCount.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-rose">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Failed</div>
              <div className="stat-value text-error">{report.failCount.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className={`stat-icon ${passRate >= 90 ? 'stat-icon-emerald' : passRate >= 70 ? 'stat-icon-amber' : 'stat-icon-rose'}`}>
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Pass Rate</div>
              <div className={`stat-value ${passRate >= 90 ? 'text-success' : passRate >= 70 ? 'text-warning' : 'text-error'}`}>
                {passRate}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report details */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Report Information</h2>
          <dl className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">Reporter</dt>
              <dd className="font-medium">{report.orgName}</dd>
            </div>
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">Report ID</dt>
              <dd className="font-mono text-sm">{report.reportId}</dd>
            </div>
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">Domain</dt>
              <dd className="font-mono text-sm">{report.policyDomain}</dd>
            </div>
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">Date Range</dt>
              <dd>{formatDateRange(report.dateRangeBegin, report.dateRangeEnd)}</dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-muted-foreground">Received</dt>
              <dd>{formatDate(report.createdAt)}</dd>
            </div>
          </dl>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">DMARC Policy</h2>
          <dl className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">Policy (p)</dt>
              <dd>
                <span className={`badge ${
                  report.policyP === 'reject' ? 'badge-success' :
                  report.policyP === 'quarantine' ? 'badge-warning' :
                  'badge-error'
                }`}>
                  {report.policyP}
                </span>
              </dd>
            </div>
            {report.policySp && (
              <div className="flex justify-between py-2 border-b">
                <dt className="text-muted-foreground">Subdomain Policy (sp)</dt>
                <dd>
                  <span className={`badge ${
                    report.policySp === 'reject' ? 'badge-success' :
                    report.policySp === 'quarantine' ? 'badge-warning' :
                    'badge-error'
                  }`}>
                    {report.policySp}
                  </span>
                </dd>
              </div>
            )}
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">DKIM Alignment</dt>
              <dd className="font-mono">{report.policyAdkim === 'r' ? 'relaxed' : 'strict'}</dd>
            </div>
            <div className="flex justify-between py-2 border-b">
              <dt className="text-muted-foreground">SPF Alignment</dt>
              <dd className="font-mono">{report.policyAspf === 'r' ? 'relaxed' : 'strict'}</dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-muted-foreground">Percentage (pct)</dt>
              <dd>{report.policyPct}%</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Records table */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Source Records</h2>
          <p className="text-sm text-muted-foreground">{report.records?.length || 0} unique sources</p>
        </div>

        {report.records && report.records.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr className="table-header">
                  <th className="table-header-cell">Source IP</th>
                  <th className="table-header-cell">Location</th>
                  <th className="table-header-cell text-right">Count</th>
                  <th className="table-header-cell">DMARC</th>
                  <th className="table-header-cell">SPF</th>
                  <th className="table-header-cell">DKIM</th>
                  <th className="table-header-cell">Disposition</th>
                </tr>
              </thead>
              <tbody>
                {report.records.map((record) => {
                  const lookup = sourceLookups?.[record.sourceIp];
                  const isExpanded = expandedIp === record.sourceIp;
                  const hasFailed = !record.dmarcPass;

                  return (
                    <>
                      <tr
                        key={record.id}
                        className={`table-row ${hasFailed ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                        onClick={() => hasFailed && setExpandedIp(isExpanded ? null : record.sourceIp)}
                      >
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            {hasFailed && (
                              <svg
                                className={`icon-sm text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                              </svg>
                            )}
                            <div>
                              <div className="font-mono text-sm">{record.sourceIp}</div>
                              {record.asnOrg && (
                                <div className="text-xs text-muted-foreground">{record.asnOrg}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell text-sm text-muted-foreground">
                          {record.country || '-'}
                        </td>
                        <td className="table-cell text-right font-medium">
                          {record.count.toLocaleString()}
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${record.dmarcPass ? 'badge-success' : 'badge-error'}`}>
                            {record.dmarcPass ? 'pass' : 'fail'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <span className={`badge ${record.spfResult === 'pass' ? 'badge-success' : 'badge-error'}`}>
                              {record.spfResult || 'none'}
                            </span>
                            {record.spfAligned && (
                              <span className="text-xs text-success" title="Aligned">A</span>
                            )}
                          </div>
                          {record.spfDomain && (
                            <div className="text-xs text-muted-foreground font-mono mt-1">{record.spfDomain}</div>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <span className={`badge ${record.dkimResult === 'pass' ? 'badge-success' : 'badge-error'}`}>
                              {record.dkimResult || 'none'}
                            </span>
                            {record.dkimAligned && (
                              <span className="text-xs text-success" title="Aligned">A</span>
                            )}
                          </div>
                          {record.dkimDomain && (
                            <div className="text-xs text-muted-foreground font-mono mt-1">
                              {record.dkimDomain}
                              {record.dkimSelector && ` (${record.dkimSelector})`}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${
                            record.disposition === 'none' ? 'badge-success' :
                            record.disposition === 'quarantine' ? 'badge-warning' :
                            'badge-error'
                          }`}>
                            {record.disposition}
                          </span>
                        </td>
                      </tr>
                      {/* Expanded recommendation row */}
                      {isExpanded && hasFailed && (
                        <tr key={`${record.id}-expanded`} className="bg-amber-50 dark:bg-amber-950/20">
                          <td colSpan={7} className="p-4">
                            <div className="flex items-start gap-4">
                              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                                <svg className="icon-md text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                                </svg>
                              </div>
                              <div className="flex-1 space-y-3">
                                {lookup?.matchedService ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">Identified as:</span>
                                      <span className="badge badge-primary">{lookup.matchedService.name}</span>
                                      <span className="text-sm text-muted-foreground">({lookup.matchedService.category})</span>
                                    </div>
                                    {lookup.recommendation && (
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <div className="flex items-center gap-2 mb-2">
                                          <svg className="icon-sm text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                          </svg>
                                          <span className="font-medium text-sm">Recommended Fix</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{lookup.recommendation}</p>
                                        {lookup.matchedService.spfInclude && (
                                          <div className="mt-3 flex items-center gap-2">
                                            <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                                              include:{lookup.matchedService.spfInclude}
                                            </code>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(`include:${lookup.matchedService!.spfInclude}`);
                                              }}
                                              className="btn btn-sm btn-secondary"
                                            >
                                              Copy
                                            </button>
                                          </div>
                                        )}
                                        {lookup.matchedService.dkimSetupUrl && (
                                          <a
                                            href={lookup.matchedService.dkimSetupUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 mt-3 text-sm text-indigo-600 hover:text-indigo-500"
                                          >
                                            View {lookup.matchedService.name} DKIM setup guide
                                            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                            </svg>
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : lookup?.asnOrg ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">Hosted on:</span>
                                      <span className="text-muted-foreground">{lookup.asnOrg}</span>
                                    </div>
                                    {lookup.ptr && (
                                      <div className="text-sm text-muted-foreground">
                                        <span className="font-medium">PTR:</span>{' '}
                                        <code className="font-mono">{lookup.ptr}</code>
                                      </div>
                                    )}
                                    {lookup.recommendation && (
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <p className="text-sm text-muted-foreground">{lookup.recommendation}</p>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-sm text-muted-foreground">
                                    <p>Unable to identify this sender. This could be:</p>
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                      <li>A legitimate service you use that we haven't identified yet</li>
                                      <li>An unauthorized sender attempting to spoof your domain</li>
                                    </ul>
                                    <p className="mt-2">Check if you recognize this IP address or contact your IT team.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No source records found
          </div>
        )}
      </div>

      {/* Back link */}
      <div>
        <Link to="/reports" className="text-indigo-600 hover:text-indigo-500 font-medium">
          ← Back to Reports
        </Link>
      </div>
    </div>
  );
}
